import { useEffect } from 'react';
import {
  LINE_SCROLL_PX,
  SCROLL_BEHAVIOR_RESTORE_MS,
  TRACKPAD_WHEEL_MULTIPLIER,
  WHEEL_KIND_RESET_MS,
  WHEEL_SPEED_MULTIPLIER,
} from './scrollControl.constants';
import { ScrollGestureState } from './scrollGestureState';
import { resolveWheelInputKind } from './wheelInputKind';

// Horizontal intent only (sideways swipe, shift+wheel). Vertical wheeling is deliberately left to
// the page; hijacking it would trap the reader in the carousel.
export const useHorizontalWheelScroll = (
  scrollContainerRef: React.RefObject<HTMLDivElement>,
  { drag, programmatic, wheel }: ScrollGestureState,
) => {
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) {
      return undefined;
    }

    const programmaticScroll = programmatic.current;

    const handleWheel = (event: WheelEvent) => {
      if (drag.current.isDragging) {
        return;
      }

      const { deltaX, deltaY, deltaMode, shiftKey } = event;
      const absDeltaX = Math.abs(deltaX);
      const absDeltaY = Math.abs(deltaY);
      const hasHorizontalIntent = absDeltaX > absDeltaY;
      const hasShiftHorizontal = shiftKey && absDeltaY > 0;

      if (!hasHorizontalIntent && !hasShiftHorizontal) {
        return;
      }

      const delta = hasShiftHorizontal && !hasHorizontalIntent ? deltaY : deltaX;
      if (delta === 0) {
        return;
      }

      const toPixels = (value: number) => {
        if (deltaMode === 1) {
          return value * LINE_SCROLL_PX;
        }
        if (deltaMode === 2) {
          return value * scrollContainer.clientWidth;
        }
        return value;
      };
      const deltaInPx = toPixels(delta);
      const maxDeltaInPx = toPixels(Math.max(absDeltaX, absDeltaY));
      const hasFractionalDelta = !Number.isInteger(deltaX) || !Number.isInteger(deltaY) || !Number.isInteger(delta);

      const wheelState = wheel.current;
      if (wheelState.inputKindResetId !== null) {
        window.clearTimeout(wheelState.inputKindResetId);
      }
      wheelState.inputKindResetId = window.setTimeout(() => {
        wheelState.inputKind = null;
        wheelState.inputKindResetId = null;
      }, WHEEL_KIND_RESET_MS);

      wheelState.inputKind = resolveWheelInputKind(wheelState.inputKind, {
        deltaMode,
        maxDeltaInPx,
        hasFractionalDelta,
      });

      const speedMultiplier = wheelState.inputKind === 'trackpad' ? TRACKPAD_WHEEL_MULTIPLIER : WHEEL_SPEED_MULTIPLIER;

      programmaticScroll.isActive = false;
      // Smooth scrolling fights per-event deltas; restored once the wheel goes quiet.
      scrollContainer.style.scrollBehavior = 'auto';
      if (wheelState.restoreTimeoutId !== null) {
        window.clearTimeout(wheelState.restoreTimeoutId);
      }
      wheelState.restoreTimeoutId = window.setTimeout(() => {
        scrollContainer.style.scrollBehavior = '';
        wheelState.restoreTimeoutId = null;
      }, SCROLL_BEHAVIOR_RESTORE_MS);
      scrollContainer.scrollLeft += deltaInPx * speedMultiplier;
      event.preventDefault();
    };

    scrollContainer.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      scrollContainer.removeEventListener('wheel', handleWheel);
    };
  }, [scrollContainerRef, drag, programmatic, wheel]);
};
